import {useCallback, useEffect, useState} from 'react';

const STEP_QUERY = 'step';

// URL ↔ internal page index translation. The UI labels and the URL are
// 1-indexed ("Step 2 of 2", `?step=2`), the internal page index is
// 0-indexed (`page === 0` for the first page). Translate at the
// boundary so neither layer leaks the other's convention.
function readStep() {
    try {
        const n = parseInt(new URLSearchParams(window.location.search).get(STEP_QUERY), 10);
        return Number.isFinite(n) && n >= 1 ? n - 1 : 0;
    } catch {
        return 0;
    }
}

function writeStep(page) {
    try {
        const url = new URL(window.location.href);
        const human = page + 1;
        if (human > 1) url.searchParams.set(STEP_QUERY, String(human));
        else url.searchParams.delete(STEP_QUERY);
        window.history.replaceState(window.history.state, '', url.toString());
    } catch { /* SSR / sandboxed iframe — ignore */ }
}

// Single source of truth for the resume order wizard step. The URL
// (`?step=`) owns the value (1-indexed for human-readability); component
// state mirrors it as 0-indexed for ergonomic React conditionals.
export default function useFormStep() {
    const [step, setStepState] = useState(readStep);

    const setStep = useCallback((next) => {
        writeStep(next);
        setStepState(next);
    }, []);

    useEffect(() => {
        const onPop = () => setStepState(readStep());
        window.addEventListener('popstate', onPop);
        return () => window.removeEventListener('popstate', onPop);
    }, []);

    return [step, setStep];
}
